import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Select, { SingleValue } from 'react-select';
import {
  ArrowLeft, RefreshCw, CalendarDays, Clock, Users,
  Plus, Search, X, ChevronDown,
  LayoutGrid, Loader2, Check, MapPin, List,
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import {
  commonAreaReservationService,
  CommonArea,
  CommonAreaBooking,
  CommonAreaResource,
  BookingStatus,
} from '../../../services/commonAreaReservationService';
import { BookingCheckinPanel } from '../../commonAreas/BookingCheckinPanel';
import { fetchWithAuth, makeAbsolutePhotoUrl, condoApi } from '../../../services/api';
import { usePersonDetailContext } from '../../../contexts/PersonDetailContext';
import toast from 'react-hot-toast';
import NovaReservaModal from '../../commonAreas/NovaReservaModal';
import BookingActions from '../../commonAreas/BookingActions';
import BookingIdChip from '../../commonAreas/BookingIdChip';
import BookingsCalendar from '../../commonAreas/BookingsCalendar';
import { bookingStatusChip, noticeChip } from '../../commonAreas/bookingStatusStyles';
import ReservaDetailModal from '../../commonAreas/ReservaDetailModal';
import { bookingCalendarDate, operatingHoursForDate, wallClock, wholeDayLabel } from '../../../pages/bookingWhen';
import { bookingChipTimeLabel, bookingDayLabel, bookingHoursLabel } from '../../commonAreas/bookingLabels';
import { searchBookings } from '../../commonAreas/bookingListView';
import { areaMainPhoto } from '../../commonAreas/areaPhotos';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AreasComunsProps {
  customerId: string;
  onBack: () => void;
  lightTheme?: boolean;
}

type ActiveTab = 'overview' | 'bookings';

interface RevokeModalState {
  open: boolean;
  booking: CommonAreaBooking | null;
  reason: string;
  submitting: boolean;
}

// ─── Searchable select helpers (same react-select pattern as VisitorRegistrationModal /
// UnitAssignmentSection — type-to-filter instead of a plain native <select>) ────────────

type SelectOpt = SingleValue<{ value: string; label: string }>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * The hours as WRITTEN in the timestamp — never `new Date(iso)`.
 *
 * A booking's times are a wall clock, not an instant: the clients send the hours the resident
 * picked with a `Z` on the end (Android with -03:00, meaning the same thing). Re-reading that
 * through a Date shifted it into the browser's zone, so a 09:00 slot showed as 06:00.
 */
const fmtTime = (iso: string) => wallClock(iso) ?? '--:--';

// The day and the hours come from the shared labels (bookingLabels.ts). This file carried its own
// copy of both, which is how the time-slot hours could be fixed in one place and stay wrong here.
const bookingDay = bookingDayLabel;
const bookingHours = bookingHoursLabel;

/** "25/10 14:00" — a booking's own day and hour, both read off the string. */
const fmtDateTime = (iso: string) => {
  const date = bookingCalendarDate(iso);
  if (!date) return '--';
  return `${date.slice(8, 10)}/${date.slice(5, 7)} ${fmtTime(iso)}`;
};

const toDateStr = (d: Date) => d.toISOString().split('T')[0];

const isActiveNow = (b: CommonAreaBooking) => {
  const now = Date.now();
  return new Date(b.start_time).getTime() <= now && new Date(b.end_time).getTime() >= now;
};

const isUpcoming = (b: CommonAreaBooking) => new Date(b.start_time).getTime() > Date.now();

type AreaStatus = { type: 'occupied'; label: string } | { type: 'available'; label: string } | { type: 'soon'; label: string; minutesUntil: number };

const getAreaStatus = (areaId: string, bookings: CommonAreaBooking[]): AreaStatus => {
  const relevant = bookings.filter(b => b.common_area_id === areaId && ['Confirmed', 'Approved'].includes(b.status));
  const active = relevant.find(isActiveNow);
  if (active) return { type: 'occupied', label: 'Em uso agora' };
  const next = relevant.filter(isUpcoming).sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())[0];
  if (next) {
    const mins = Math.floor((new Date(next.start_time).getTime() - Date.now()) / 60000);
    const label = mins < 60 ? `Em ${mins} min` : `Às ${fmtTime(next.start_time)}`;
    return { type: 'soon', label, minutesUntil: mins };
  }
  return { type: 'available', label: 'Disponível' };
};

const personName = (b: CommonAreaBooking) =>
  b.person ? `${b.person.first_name} ${b.person.last_name}`.trim() : 'Morador';

/**
 * Unit shown for a booking.
 *
 * Prefers unit_label, the snapshot frozen on the booking when it was made (migration 217). The
 * role_metadata fallback below is derived LIVE from the person's current role, so it silently
 * relabels historical reservations when a resident moves unit or loses the role — and it is what
 * the reservation e-mail no longer uses. The fallback stays for bookings created before the
 * snapshot existed.
 */
const personUnitLabel = (b: CommonAreaBooking): string | null => {
  if (b.unit_label) return b.unit_label;
  const role = b.person?.roles?.[0];
  if (!role?.role_metadata) return null;
  const unit = role.role_metadata.unit_number;
  const block = role.role_metadata.block_name;
  if (!unit) return null;
  return block ? `${block} – ${unit}` : unit;
};

const personUnitId = (b: CommonAreaBooking): string | null => {
  if (b.unit_id) return b.unit_id;
  const meta = b.person?.roles?.[0]?.role_metadata;
  if (!meta) return null;
  return meta.unit_ids?.[0] ?? meta.unit_id ?? null;
};

// ─── Date mask helper ─────────────────────────────────────────────────────────

// ─── StatusBadge ─────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: BookingStatus; lightTheme?: boolean }> = ({ status, lightTheme }) => {
  // Neutral chip, coloured dot — see bookingStatusStyles for why amber-on-amber is gone.
  const { label, chip, dot } = bookingStatusChip(status, !!lightTheme);
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border', chip)}>
      <span className={cn('h-1.5 w-1.5 rounded-full flex-shrink-0', dot)} />
      {label}
    </span>
  );
};

// ─── BookingPersonAvatar ──────────────────────────────────────────────────────

const BookingPersonAvatar: React.FC<{ person?: { first_name: string; last_name: string; profile_picture_url?: string; identity_photo?: { photo_url?: string } }; lightTheme?: boolean; active?: boolean }> = ({ person, lightTheme, active }) => {
  const [imgFailed, setImgFailed] = React.useState(false);
  const photoUrl = makeAbsolutePhotoUrl(person?.identity_photo?.photo_url || person?.profile_picture_url);
  const initials = person ? person.first_name.charAt(0).toUpperCase() : '?';
  const ringCls = active ? 'ring-2 ring-red-500/40' : lightTheme ? 'ring-1 ring-gray-200' : 'ring-1 ring-gray-700';
  const fallbackCls = cn('w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0', ringCls,
    active ? 'bg-red-500/20 text-red-400' : lightTheme ? 'bg-blue-100 text-blue-700' : 'bg-blue-900/40 text-blue-300'
  );
  if (photoUrl && !imgFailed) {
    return <img src={photoUrl} alt={person?.first_name} className={cn('w-8 h-8 rounded-full object-cover flex-shrink-0', ringCls)} onError={() => setImgFailed(true)} />;
  }
  return <div className={fallbackCls}>{initials}</div>;
};

// The portaria's booking form lives in components/commonAreas/NovaReservaModal — shared with
// the normal Reservas page, photo-first like the person modal, and aware of whether an área is
// booked by the day or by the hour. The 620-line copy that used to sit here knew none of that:
// it demanded start/end times for every área and stamped a UTC `Z` on a local wall clock.

// ─── AreaCard ────────────────────────────────────────────────────────────────

const AreaCard: React.FC<{
  area: CommonArea;
  bookings: CommonAreaBooking[];
  lightTheme?: boolean;
  onApprove: (b: CommonAreaBooking) => void;
  onRevoke: (b: CommonAreaBooking) => void;
  onBook: (areaId: string) => void;
  onView: (areaId: string) => void;
  processing: Set<string>;
}> = ({ area, bookings, lightTheme, onApprove, onRevoke, onBook, onView, processing }) => {
  const areaBookings = bookings.filter(b => b.common_area_id === area.id);
  const status = getAreaStatus(area.id, bookings);
  const pending = areaBookings.filter(b => b.status === 'Pending');
  const todayActive = areaBookings.filter(b => ['Confirmed', 'Approved'].includes(b.status)).slice(0, 4);

  const statusColor = status.type === 'occupied'
    ? 'text-red-500 bg-red-500/10 border-red-500/30'
    : status.type === 'soon'
      ? (lightTheme ? 'text-gray-700 bg-gray-100 border-gray-200' : 'text-gray-200 bg-gray-800 border-gray-700')
      : lightTheme
        ? 'text-emerald-700 bg-emerald-50 border-emerald-300'
        : 'text-emerald-400 bg-emerald-900/20 border-emerald-700/40';

  const cardBase = lightTheme
    ? 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md cursor-pointer'
    : 'bg-gray-900 border-gray-800 hover:border-blue-700 hover:shadow-md cursor-pointer';

  const photo = areaMainPhoto(area);

  return (
    <div
      className={cn('rounded-xl border p-4 flex flex-col gap-3 transition-all group', cardBase)}
      onClick={() => onView(area.id)}
      title={`Ver detalhes — ${area.name}`}
    >
      {/* The space itself. Only the main photo: this is a list row, and a carousel here would
          compete with the booking actions below it — the rest of the photos are in the modal. */}
      {photo && (
        <div className={cn('-mx-4 -mt-4 mb-1 h-28 overflow-hidden rounded-t-xl border-b',
          lightTheme ? 'border-gray-200 bg-gray-100' : 'border-gray-800 bg-gray-950')}>
          <img
            src={photo}
            alt={area.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
          />
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className={cn('font-semibold text-sm leading-tight truncate', lightTheme ? 'text-gray-900' : 'text-white')}>
            {area.name}
          </h3>
          {area.description && (
            <p className={cn('text-xs mt-0.5 line-clamp-1', lightTheme ? 'text-gray-500' : 'text-gray-500')}>
              {area.description}
            </p>
          )}
        </div>
        <span className={cn('flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold border', statusColor)}>
          {status.label}
        </span>
      </div>

      {/* Meta */}
      <div className="flex items-center gap-3">
        <span className={cn('flex items-center gap-1 text-xs', lightTheme ? 'text-gray-500' : 'text-gray-500')}>
          <Users className="h-3.5 w-3.5" />
          {area.capacity} pessoas
        </span>
        {area.opening_time && area.closing_time && (
          <span className={cn('flex items-center gap-1 text-xs', lightTheme ? 'text-gray-500' : 'text-gray-500')}>
            <Clock className="h-3.5 w-3.5" />
            {area.opening_time} – {area.closing_time}
          </span>
        )}
      </div>

      {/* Pending approvals */}
      {pending.length > 0 && (
        <div
          className={cn('rounded-lg p-2 border', lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-800/60 border-gray-700')}
          onClick={e => e.stopPropagation()}
        >
          <p className={cn('flex items-center gap-1.5 text-xs font-medium mb-1.5', lightTheme ? 'text-gray-700' : 'text-gray-200')}>
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" />
            {pending.length} {pending.length === 1 ? 'reserva pendente' : 'reservas pendentes'}
          </p>
          <div className="flex flex-col gap-1">
            {pending.slice(0, 2).map(b => (
              <div key={b.id} className="flex items-center justify-between gap-2">
                <span className={cn('text-xs truncate', lightTheme ? 'text-gray-600' : 'text-gray-300')}>
                  {personName(b)} · {bookingChipTimeLabel(b, area)}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); onApprove(b); }}
                  disabled={processing.has(b.id)}
                  className={cn(
                    'flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors disabled:opacity-50',
                    lightTheme
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-emerald-700 hover:bg-emerald-600 text-white'
                  )}
                >
                  {processing.has(b.id) ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  Aprovar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Today's schedule */}
      {todayActive.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className={cn('text-xs font-medium', lightTheme ? 'text-gray-400' : 'text-gray-500')}>Agendamentos hoje</p>
          {todayActive.map(b => (
            <div key={b.id} className={cn('flex items-center justify-between gap-2 px-2 py-1 rounded-lg', lightTheme ? 'bg-gray-50' : 'bg-gray-800/60')}>
              <div className="flex items-center gap-1.5 min-w-0">
                <div className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', isActiveNow(b) ? 'bg-red-500 animate-pulse' : 'bg-blue-500')} />
                <span className={cn('text-xs truncate', lightTheme ? 'text-gray-700' : 'text-gray-300')}>
                  {personName(b)}
                </span>
              </div>
              <span className={cn('text-xs flex-shrink-0', lightTheme ? 'text-gray-500' : 'text-gray-500')}>
                {/* A whole-day reservation has no hours anybody chose: bookingHours says the
                    space's window instead of the stored 00:00–23:59. */}
                {bookingHours(b, area)}
              </span>
            </div>
          ))}
        </div>
      )}

      {todayActive.length === 0 && pending.length === 0 && (
        <p className={cn('text-xs text-center py-1', lightTheme ? 'text-gray-400' : 'text-gray-600')}>
          Nenhum agendamento hoje
        </p>
      )}

      {/* Reservar button — always visible */}
      <button
        onClick={(e) => { e.stopPropagation(); onBook(area.id); }}
        className={cn(
          'w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-colors border mt-auto',
          lightTheme
            ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:border-blue-300'
            : 'bg-blue-900/20 border-blue-800/50 text-blue-400 hover:bg-blue-900/40 hover:border-blue-700'
        )}
      >
        <Plus className="h-3.5 w-3.5" />
        Reservar
      </button>
    </div>
  );
};

// ─── BookingRow ───────────────────────────────────────────────────────────────

const BookingRow: React.FC<{
  booking: CommonAreaBooking;
  areas: CommonArea[];
  lightTheme?: boolean;
  onApprove: (b: CommonAreaBooking) => void;
  onRevoke: (b: CommonAreaBooking) => void;
  onCancel: (b: CommonAreaBooking) => void;
  onViewPerson?: (personId: string) => void;
  /** Opens the reservation's own modal. */
  onOpen?: (b: CommonAreaBooking) => void;
  processing: Set<string>;
}> = ({ booking: b, areas, lightTheme, onApprove, onRevoke, onCancel, onViewPerson, onOpen, processing }) => {
  const { onUnitClick } = usePersonDetailContext();
  const area = areas.find(a => a.id === b.common_area_id);
  const isProc = processing.has(b.id);
  // Collapsed by default: the list is scanned far more often than a single reservation is
  // checked in, and the panel fetches guests + checklist when opened.
  const [expanded, setExpanded] = useState(false);
  const [checkedInAt, setCheckedInAt] = useState<string | undefined>(b.checked_in_at);
  const canApprove = b.status === 'Pending';
  const canRevoke = ['Confirmed', 'Approved'].includes(b.status);
  const canCancel = ['Pending', 'Confirmed', 'Approved'].includes(b.status);

  const rowBase = lightTheme
    ? 'border-b border-gray-100 hover:bg-gray-50'
    : 'border-b border-gray-800/60 hover:bg-gray-800/40';

  return (
    <div className={rowBase}>
    <div className="flex items-center gap-3 px-4 py-3 transition-colors">
      {/* Person avatar */}
      <BookingPersonAvatar person={b.person} lightTheme={lightTheme} />

      {/* Info — the whole block opens the reservation; the person and unit links inside it keep
          their own destinations (they stopPropagation). */}
      <div
        role="button"
        tabIndex={0}
        aria-label={`Abrir reserva de ${personName(b)}`}
        onClick={() => onOpen?.(b)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(b); } }}
        className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-0.5 items-center text-left cursor-pointer"
      >
        <div className="col-span-2 md:col-span-1">
          {onViewPerson && b.person_id ? (
            <button
              onClick={e => { e.stopPropagation(); onViewPerson(b.person_id!); }}
              className={cn('text-sm font-medium truncate text-left hover:underline', lightTheme ? 'text-gray-900 hover:text-blue-600' : 'text-white hover:text-blue-400')}
            >
              {personName(b)}
            </button>
          ) : (
            <p className={cn('text-sm font-medium truncate', lightTheme ? 'text-gray-900' : 'text-white')}>
              {personName(b)}
            </p>
          )}
          {personUnitLabel(b) && (
            onUnitClick && personUnitId(b) ? (
              <button
                onClick={e => { e.stopPropagation(); onUnitClick(personUnitId(b)!, personUnitLabel(b)!); }}
                className={cn('text-xs truncate font-medium text-left hover:underline', lightTheme ? 'text-blue-600 hover:text-blue-800' : 'text-blue-400 hover:text-blue-300')}
              >
                {personUnitLabel(b)}
              </button>
            ) : (
              <p className={cn('text-xs truncate font-medium', lightTheme ? 'text-blue-600' : 'text-blue-400')}>
                {personUnitLabel(b)}
              </p>
            )
          )}
          {b.purpose && (
            <p className={cn('text-xs truncate', lightTheme ? 'text-gray-500' : 'text-gray-500')}>
              {b.purpose}
            </p>
          )}
        </div>
        <div className="hidden md:block">
          <p className={cn('text-xs font-medium truncate', lightTheme ? 'text-gray-700' : 'text-gray-300')}>
            {area?.name ?? '—'}
          </p>
        </div>
        <div>
          <p className={cn('text-xs', lightTheme ? 'text-gray-600' : 'text-gray-400')}>
            {bookingDay(b)}
          </p>
          <p className={cn('text-xs', lightTheme ? 'text-gray-500' : 'text-gray-500')}>
            {bookingHours(b, area)}
          </p>
          <BookingIdChip bookingId={b.id} lightTheme={lightTheme} />
        </div>
        <div className="flex items-center">
          <StatusBadge status={b.status} lightTheme={lightTheme} />
        </div>
      </div>

      {/* Actions */}
      <BookingActions
        lightTheme={lightTheme}
        processing={isProc}
        onApprove={canApprove ? () => onApprove(b) : undefined}
        onRevoke={canRevoke ? () => onRevoke(b) : undefined}
        onCancel={canCancel && !canRevoke ? () => onCancel(b) : undefined}
        onToggleDetails={() => setExpanded(v => !v)}
        detailsOpen={expanded}
        checkedIn={!!checkedInAt}
      />
    </div>

    {expanded && area && (
      <div className={cn('px-4 pb-4 pt-1', lightTheme ? 'bg-gray-50/60' : 'bg-gray-900/40')}>
        <BookingCheckinPanel
          area={area}
          booking={b}
          variant="staff"
          darkMode={!lightTheme}
          onUpdated={patch => setCheckedInAt(patch.checked_in_at)}
        />
      </div>
    )}
    </div>
  );
};

// ─── AreaDetailView ───────────────────────────────────────────────────────────

const AreaDetailView: React.FC<{
  area: CommonArea;
  lightTheme?: boolean;
  refreshKey: number;
  onBack: () => void;
  onBook: () => void;
  onApprove: (b: CommonAreaBooking) => void;
  onRevoke: (b: CommonAreaBooking) => void;
  onCancel: (b: CommonAreaBooking) => void;
  onViewPerson?: (personId: string) => void;
  /** Opens a reservation's own modal — the same card the Agendamentos list opens. */
  onOpenBooking?: (b: CommonAreaBooking) => void;
  processing: Set<string>;
}> = ({ area, lightTheme = false, refreshKey, onBack, onBook, onApprove, onRevoke, onCancel, onViewPerson, onOpenBooking, processing }) => {
  const { onUnitClick } = usePersonDetailContext();
  const [bookings, setBookings] = useState<CommonAreaBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // One reservation expanded at a time. renderBookingItem below is a function, not a component,
  // so it cannot own per-row state without breaking the rules of hooks.
  const [expandedBookingId, setExpandedBookingId] = useState<string | null>(null);

  const fetchBookings = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true); else setRefreshing(true);
    try {
      const from = toDateStr(new Date(Date.now() - 30 * 86400000));
      const to = toDateStr(new Date(Date.now() + 90 * 86400000));
      const data = await commonAreaReservationService.getBookingsForArea(area.id, {
        from: `${from}T00:00:00Z`,
        to: `${to}T23:59:59Z`,
      });
      setBookings(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Erro ao carregar reservas');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [area.id]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);
  useEffect(() => { if (refreshKey > 0) fetchBookings(true); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = getAreaStatus(area.id, bookings);
  // An área with a year of history is a scroll; the same search the reservations page uses,
  // reading the same fields (searchBookings), narrows both sections at once.
  const [query, setQuery] = useState('');
  const visible = useMemo(() => searchBookings(bookings, query, [area]), [bookings, query, area]);

  const now = Date.now();
  const upcoming = useMemo(() =>
    visible
      .filter(b => ['Pending', 'Confirmed', 'Approved'].includes(b.status) && new Date(b.end_time).getTime() > now)
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()),
    [visible] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const history = useMemo(() =>
    visible
      .filter(b => !upcoming.find(u => u.id === b.id))
      .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime()),
    [visible, upcoming]
  );

  const detailPhoto = areaMainPhoto(area);

  // theme
  const bg = lightTheme ? 'bg-white' : 'bg-gray-950';
  const borderCls = lightTheme ? 'border-gray-200' : 'border-gray-800';
  const headerBg = lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-900 border-gray-800';
  const text = lightTheme ? 'text-gray-900' : 'text-white';
  const subtext = lightTheme ? 'text-gray-500' : 'text-gray-400';

  const statusColor = status.type === 'occupied'
    ? 'text-red-500 bg-red-500/10 border-red-500/30'
    : status.type === 'soon'
      ? (lightTheme ? 'text-gray-700 bg-gray-100 border-gray-200' : 'text-gray-200 bg-gray-800 border-gray-700')
      : lightTheme
        ? 'text-emerald-700 bg-emerald-50 border-emerald-300'
        : 'text-emerald-400 bg-emerald-900/20 border-emerald-700/40';

  const sectionHeader = (label: string, count: number, accent?: string) => (
    <div className={cn('flex items-center gap-2 px-4 py-2 border-b', lightTheme ? 'bg-gray-50 border-gray-100' : 'bg-gray-900/60 border-gray-800/60')}>
      <span className={cn('text-xs font-semibold uppercase tracking-wider', subtext)}>{label}</span>
      <span className={cn('px-1.5 py-0.5 rounded-full text-xs font-semibold',
        accent || (lightTheme ? 'bg-gray-200 text-gray-500' : 'bg-gray-800 text-gray-500')
      )}>
        {count}
      </span>
    </div>
  );

  const renderBookingItem = (b: CommonAreaBooking, isUpcoming: boolean) => {
    const isProc = processing.has(b.id);
    const active = isActiveNow(b);
    const canApprove = b.status === 'Pending';
    const canRevoke = ['Confirmed', 'Approved'].includes(b.status);
    const canCancel = ['Pending', 'Confirmed'].includes(b.status) && isUpcoming && !canRevoke;

    const expanded = expandedBookingId === b.id;

    return (
      <div key={b.id} className={cn(
        'border-b',
        lightTheme ? 'border-gray-100' : 'border-gray-800/60'
      )}>
      <div className={cn(
        'flex items-center gap-3 px-4 py-3 transition-colors',
        lightTheme ? 'hover:bg-gray-50' : 'hover:bg-gray-800/30'
      )}>
        {/* Avatar */}
        <BookingPersonAvatar person={b.person} lightTheme={lightTheme} active={active} />

        {/* Info — opens the reservation; the person and unit links inside keep their own
            destinations (they stopPropagation). */}
        <div
          role="button"
          tabIndex={0}
          aria-label={`Abrir reserva de ${personName(b)}`}
          onClick={() => onOpenBooking?.(b)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenBooking?.(b); } }}
          className="flex-1 min-w-0 text-left cursor-pointer"
        >
          <div className="flex items-center gap-2">
            {onViewPerson && b.person_id ? (
              <button
                onClick={e => { e.stopPropagation(); onViewPerson(b.person_id!); }}
                className={cn('text-sm font-medium truncate text-left hover:underline', lightTheme ? 'text-gray-900 hover:text-blue-600' : 'text-white hover:text-blue-400')}
              >
                {personName(b)}
              </button>
            ) : (
              <p className={cn('text-sm font-medium truncate', text)}>{personName(b)}</p>
            )}
            {active && (
              <span className="flex items-center gap-1 text-xs text-red-400 font-semibold flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse inline-block" />
                Em uso
              </span>
            )}
          </div>
          {personUnitLabel(b) && (
            onUnitClick && personUnitId(b) ? (
              <button
                onClick={e => { e.stopPropagation(); onUnitClick(personUnitId(b)!, personUnitLabel(b)!); }}
                className={cn('text-xs truncate font-medium text-left hover:underline', lightTheme ? 'text-blue-600 hover:text-blue-800' : 'text-blue-400 hover:text-blue-300')}
              >
                {personUnitLabel(b)}
              </button>
            ) : (
              <p className={cn('text-xs truncate font-medium', lightTheme ? 'text-blue-600' : 'text-blue-400')}>
                {personUnitLabel(b)}
              </p>
            )
          )}
          {b.purpose && <p className={cn('text-xs truncate', subtext)}>{b.purpose}</p>}
          <p className={cn('text-xs', subtext)}>
            {bookingDay(b)} · {bookingHours(b, area)}
          </p>
          <span onClick={e => e.stopPropagation()}>
            <BookingIdChip bookingId={b.id} lightTheme={lightTheme} />
          </span>
        </div>

        {/* Status + actions.
            No "Revogar" here on purpose: revoking demands a reason and opens its own modal, and
            this view is where an operator READS an área — including its histórico, where a revoke
            button would sit next to reservations that already happened. It lives in Agendamentos. */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusBadge status={b.status} lightTheme={lightTheme} />
          <BookingActions
            lightTheme={lightTheme}
            processing={isProc}
            onApprove={canApprove ? () => onApprove(b) : undefined}
            onCancel={canCancel ? () => onCancel(b) : undefined}
            onToggleDetails={() => setExpandedBookingId(expanded ? null : b.id)}
            detailsOpen={expanded}
            checkedIn={!!b.checked_in_at}
          />
        </div>
      </div>

      {expanded && (
        <div className={cn('px-4 pb-4 pt-1', lightTheme ? 'bg-gray-50/60' : 'bg-gray-900/40')}>
          <BookingCheckinPanel
            area={area}
            booking={b}
            variant="staff"
            darkMode={!lightTheme}
            onUpdated={() => fetchBookings(true)}
          />
        </div>
      )}
      </div>
    );
  };

  return (
    <div className={cn('flex flex-col min-h-0 flex-1', bg)}>
      {/* Header */}
      <div className={cn('flex-shrink-0 border-b px-4 py-3', headerBg, borderCls)}>
        <div className="flex items-center gap-3">
          <button onClick={onBack}
            className={cn('p-1.5 rounded-lg transition-colors flex-shrink-0', lightTheme ? 'hover:bg-gray-200 text-gray-600' : 'hover:bg-gray-800 text-gray-400')}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          {/* The photo the list already loaded — redrawing the same área one click later with no
              picture is what made this screen feel unfinished. */}
          {detailPhoto && (
            <img
              src={detailPhoto}
              alt={area.name}
              loading="lazy"
              className={cn('h-10 w-14 rounded-lg object-cover border flex-shrink-0', borderCls)}
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className={cn('text-sm font-bold', text)}>{area.name}</h2>
              <span className={cn('px-2 py-0.5 rounded-full text-xs font-bold border flex-shrink-0', statusColor)}>
                {status.label}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-0.5">
              {area.capacity > 0 && (
                <span className={cn('flex items-center gap-1 text-xs', subtext)}>
                  <Users className="h-3 w-3" />{area.capacity} pessoas
                </span>
              )}
              {area.opening_time && area.closing_time && (
                <span className={cn('flex items-center gap-1 text-xs', subtext)}>
                  <Clock className="h-3 w-3" />{area.opening_time} – {area.closing_time}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={() => fetchBookings(true)} disabled={refreshing}
              className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-200 text-gray-500' : 'hover:bg-gray-800 text-gray-400')}
              title="Atualizar"
            >
              <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
            </button>
            <button onClick={onBook}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Nova Reserva</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className={cn('h-7 w-7 animate-spin', lightTheme ? 'text-blue-600' : 'text-blue-400')} />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {/* Area info strip */}
          {(area.description || area.rules || area.requires_approval) && (
            <div className={cn('px-4 py-3 border-b text-xs space-y-0.5', lightTheme ? 'border-gray-100 text-gray-500' : 'border-gray-800/60 text-gray-500')}>
              {area.description && <p>{area.description}</p>}
              {area.rules && <p>{area.rules}</p>}
              {area.requires_approval && (
                <p className={cn('flex items-center gap-1.5 font-semibold', lightTheme ? 'text-gray-700' : 'text-gray-200')}>
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                  Requer aprovação do síndico
                </p>
              )}
            </div>
          )}

          {/* One box over both sections — "where is Claudia's booking" is the question, and
              whether it is in Próximas or Histórico is the answer, not the input. */}
          {bookings.length > 3 && (
            <div className={cn('px-4 py-3 border-b', lightTheme ? 'border-gray-100' : 'border-gray-800/60')}>
              <div className="relative">
                <Search className={cn('absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none', subtext)} />
                <input
                  type="search"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Buscar morador, unidade, finalidade..."
                  aria-label="Buscar reservas desta área"
                  className={cn(
                    'w-full rounded-lg border pl-9 pr-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500',
                    lightTheme
                      ? 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      : 'bg-gray-900 border-gray-700 text-gray-100 placeholder-gray-500',
                  )}
                />
              </div>
            </div>
          )}

          {/* Próximas reservas */}
          {sectionHeader(
            'Próximas Reservas',
            upcoming.length,
            upcoming.length > 0
              ? lightTheme ? 'bg-blue-100 text-blue-600' : 'bg-blue-900/40 text-blue-400'
              : undefined
          )}
          {upcoming.length === 0 ? (
            <div className={cn('px-4 py-8 text-center text-sm', subtext)}>
              {query.trim() ? `Nenhuma reserva futura para “${query.trim()}”` : 'Nenhuma reserva futura'}
            </div>
          ) : (
            upcoming.map(b => renderBookingItem(b, true))
          )}

          {/* Histórico */}
          {history.length > 0 && (
            <>
              <div className={cn('border-t', lightTheme ? 'border-gray-200' : 'border-gray-800/60')} />
              {sectionHeader('Histórico', history.length)}
              {history.map(b => renderBookingItem(b, false))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const AreasComuns: React.FC<AreasComunsProps> = ({ customerId, onBack, lightTheme = false }) => {
  const { openPersonDetail, onUnitClick } = usePersonDetailContext();
  const [areas, setAreas] = useState<CommonArea[]>([]);
  const [bookings, setBookings] = useState<CommonAreaBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [search, setSearch] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterStatus, setFilterStatus] = useState<BookingStatus | ''>('');
  const [dateFrom, setDateFrom] = useState(() => toDateStr(new Date()));
  const [dateTo, setDateTo] = useState(() => toDateStr(new Date(Date.now() + 7 * 86400000)));

  const [bookingModal, setBookingModal] = useState<{ open: boolean; areaId: string }>({ open: false, areaId: '' });
  const [processing, setProcessing] = useState<Set<string>>(new Set());
  const [revokeModal, setRevokeModal] = useState<RevokeModalState>({
    open: false, booking: null, reason: '', submitting: false,
  });
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  // Agendamentos: a flat list, or the month. The month OWNS the date range while it is showing —
  // paging it refetches, which is why dateFrom/dateTo move with the anchor.
  const [bookingsView, setBookingsView] = useState<'list' | 'calendar'>('list');
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [detailBooking, setDetailBooking] = useState<CommonAreaBooking | null>(null);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);

  // ── Fetch data ──────────────────────────────────────────────────────────────

  const fetchData = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    else setRefreshing(true);
    try {
      const [areasRes, bookingsData] = await Promise.all([
        fetchWithAuth(`/api/v1/customers/${customerId}/common-areas`),
        commonAreaReservationService.getAllBookings(customerId, {
          from: `${dateFrom}T00:00:00.000Z`,
          to: `${dateTo}T23:59:59.999Z`,
        }),
      ]);
      if (areasRes.ok) {
        const data = await areasRes.json();
        setAreas(Array.isArray(data) ? data : (data.data ?? []));
      }
      setBookings(Array.isArray(bookingsData) ? bookingsData : []);
    } catch {
      toast.error('Erro ao carregar áreas comuns');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [customerId, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /** Point the loaded range at a whole month — what the calendar needs to draw it. */
  const showMonth = useCallback((anchor: Date) => {
    setMonthAnchor(anchor);
    setDateFrom(toDateStr(new Date(anchor.getFullYear(), anchor.getMonth(), 1)));
    setDateTo(toDateStr(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0)));
  }, []);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const setProc = (id: string, on: boolean) =>
    setProcessing(prev => { const s = new Set(prev); on ? s.add(id) : s.delete(id); return s; });

  const bumpDetail = () => setDetailRefreshKey(k => k + 1);

  // The reservation modal reads from the loaded list, so an approve/revoke performed inside it
  // shows the new status as soon as the refetch lands instead of going stale behind the user.
  useEffect(() => {
    setDetailBooking(prev => (prev ? bookings.find(b => b.id === prev.id) ?? prev : prev));
  }, [bookings]);

  const handleApprove = async (b: CommonAreaBooking) => {
    setProc(b.id, true);
    try {
      await commonAreaReservationService.approveBooking(b.common_area_id, b.id);
      toast.success('Reserva aprovada');
      fetchData(true);
      bumpDetail();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao aprovar reserva');
    } finally {
      setProc(b.id, false);
    }
  };

  const handleRevoke = (b: CommonAreaBooking) =>
    setRevokeModal({ open: true, booking: b, reason: '', submitting: false });

  const submitRevoke = async () => {
    if (!revokeModal.booking || !revokeModal.reason.trim()) return;
    setRevokeModal(prev => ({ ...prev, submitting: true }));
    try {
      await commonAreaReservationService.revokeBooking(
        revokeModal.booking.common_area_id, revokeModal.booking.id, revokeModal.reason
      );
      toast.success('Reserva revogada');
      setRevokeModal({ open: false, booking: null, reason: '', submitting: false });
      fetchData(true);
      bumpDetail();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao revogar reserva');
      setRevokeModal(prev => ({ ...prev, submitting: false }));
    }
  };

  const handleCancel = async (b: CommonAreaBooking) => {
    setProc(b.id, true);
    try {
      await commonAreaReservationService.cancelBooking(b.common_area_id, b.id);
      toast.success('Reserva cancelada');
      fetchData(true);
      bumpDetail();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao cancelar reserva');
    } finally {
      setProc(b.id, false);
    }
  };

  // ── Derived ─────────────────────────────────────────────────────────────────

  const todayStr = toDateStr(new Date());
  const todayBookings = useMemo(
    () => bookings.filter(b => b.start_time.startsWith(todayStr)),
    [bookings, todayStr]
  );

  const filteredBookings = useMemo(() => {
    let list = bookings;
    if (filterArea) list = list.filter(b => b.common_area_id === filterArea);
    if (filterStatus) list = list.filter(b => b.status === filterStatus);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(b => personName(b).toLowerCase().includes(q) || (b.purpose || '').toLowerCase().includes(q));
    }
    return list.sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());
  }, [bookings, filterArea, filterStatus, search]);

  const stats = useMemo(() => {
    const inUse = areas.filter(a => getAreaStatus(a.id, bookings).type === 'occupied').length;
    const pending = bookings.filter(b => b.status === 'Pending').length;
    return { total: areas.length, inUse, available: areas.length - inUse, pending };
  }, [areas, bookings]);

  const selectedArea = useMemo(() => areas.find(a => a.id === selectedAreaId) ?? null, [areas, selectedAreaId]);

  // ── Theme helpers ────────────────────────────────────────────────────────────

  const bg = lightTheme ? 'bg-white' : 'bg-gray-950';
  const border = lightTheme ? 'border-gray-200' : 'border-gray-800';
  const headerBg = lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-900 border-gray-800';
  const text = lightTheme ? 'text-gray-900' : 'text-white';
  const subtext = lightTheme ? 'text-gray-500' : 'text-gray-400';
  const inputCls = lightTheme
    ? 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-blue-400'
    : 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-600 focus:border-blue-500';

  const tabBtn = (tab: ActiveTab) => cn(
    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors',
    activeTab === tab
      ? 'bg-blue-600 text-white'
      : lightTheme
        ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
        : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className={cn('flex flex-col h-full min-h-0', bg)}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className={cn('flex-shrink-0 flex flex-col border-b', headerBg, border)}>
        {/* Row 1: back + title + actions */}
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={onBack}
            className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-200 text-gray-600' : 'hover:bg-gray-800 text-gray-400')}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-2 min-w-0 flex-1">
            <h2 className={cn('text-sm font-bold truncate tracking-wide', text)}>ÁREAS COMUNS</h2>
          </div>

          {/* Stats pills */}
          <div className="hidden md:flex items-center gap-2">
            <span className={cn('px-2.5 py-1 rounded-full text-xs font-semibold border',
              lightTheme ? 'bg-gray-100 border-gray-200 text-gray-600' : 'bg-gray-800 border-gray-700 text-gray-300'
            )}>
              {stats.total} {stats.total === 1 ? 'área' : 'áreas'}
            </span>
            {stats.inUse > 0 && (
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-500/10 border border-red-500/30 text-red-500">
                {stats.inUse} em uso
              </span>
            )}
            {stats.pending > 0 && (
              <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border', noticeChip(lightTheme))}>
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                {stats.pending} pendente{stats.pending > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-200 text-gray-500' : 'hover:bg-gray-800 text-gray-400')}
              title="Atualizar"
            >
              <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
            </button>
            <button
              onClick={() => setBookingModal({ open: true, areaId: '' })}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Nova Reserva</span>
            </button>
          </div>
        </div>

        {/* Row 2: Tabs */}
        <div className="flex items-center gap-1 px-4 pb-2">
          <button onClick={() => setActiveTab('overview')} className={tabBtn('overview')}>
            <LayoutGrid className="h-3.5 w-3.5" />
            Visão Geral
          </button>
          <button onClick={() => setActiveTab('bookings')} className={tabBtn('bookings')}>
            <CalendarDays className="h-3.5 w-3.5" />
            Agendamentos
            {filteredBookings.length > 0 && (
              <span className={cn('px-1.5 py-0.5 rounded-full text-xs',
                activeTab === 'bookings' ? 'bg-blue-500 text-white' : lightTheme ? 'bg-gray-200 text-gray-600' : 'bg-gray-700 text-gray-300'
              )}>
                {filteredBookings.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────────────────────── */}

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className={cn('h-8 w-8 animate-spin', lightTheme ? 'text-blue-600' : 'text-blue-400')} />
            <p className={cn('text-sm', subtext)}>Carregando áreas comuns…</p>
          </div>
        </div>

      ) : activeTab === 'overview' ? (
        /* ── Overview ─────────────────────────────────────────────────────── */
        <div className="flex-1 overflow-y-auto p-4">
          {areas.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 py-16">
              <MapPin className={cn('h-12 w-12', lightTheme ? 'text-gray-300' : 'text-gray-700')} />
              <p className={cn('text-sm font-medium', subtext)}>Nenhuma área comum cadastrada</p>
              <p className={cn('text-xs text-center max-w-xs', lightTheme ? 'text-gray-400' : 'text-gray-600')}>
                Cadastre áreas comuns no menu Estrutura → Áreas Comuns
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {areas.map(area => (
                <AreaCard
                  key={area.id}
                  area={area}
                  bookings={todayBookings}
                  lightTheme={lightTheme}
                  onApprove={handleApprove}
                  onRevoke={handleRevoke}
                  onBook={(areaId) => setBookingModal({ open: true, areaId })}
                  onView={(areaId) => setSelectedAreaId(areaId)}
                  processing={processing}
                />
              ))}
            </div>
          )}
        </div>

      ) : (
        /* ── Bookings list ─────────────────────────────────────────────────── */
        <div className="flex-1 flex flex-col min-h-0">
          {/* Filters */}
          <div className={cn('flex-shrink-0 flex flex-wrap items-center gap-2 px-4 py-3 border-b', lightTheme ? 'bg-gray-50 border-gray-100' : 'bg-gray-900/50 border-gray-800/60')}>
            {/* Search */}
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className={cn('absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none', subtext)} />
              <input
                type="text"
                placeholder="Buscar morador…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className={cn('w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border focus:outline-none', inputCls)}
              />
            </div>

            {/* Date range */}
            <div className="flex items-center gap-1">
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className={cn('px-2 py-1.5 text-xs rounded-lg border focus:outline-none', inputCls)}
              />
              <span className={cn('text-xs', subtext)}>–</span>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className={cn('px-2 py-1.5 text-xs rounded-lg border focus:outline-none', inputCls)}
              />
            </div>

            {/* Area filter */}
            <div className="relative">
              <select
                value={filterArea}
                onChange={e => setFilterArea(e.target.value)}
                className={cn('pr-7 pl-2.5 py-1.5 text-xs rounded-lg border appearance-none focus:outline-none cursor-pointer', inputCls)}
              >
                <option value="">Todas as áreas</option>
                {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <ChevronDown className={cn('absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none', subtext)} />
            </div>

            {/* Status filter */}
            <div className="relative">
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value as BookingStatus | '')}
                className={cn('pr-7 pl-2.5 py-1.5 text-xs rounded-lg border appearance-none focus:outline-none cursor-pointer', inputCls)}
              >
                <option value="">Todos os status</option>
                <option value="Pending">Pendentes</option>
                <option value="Confirmed">Confirmados</option>
                <option value="Approved">Aprovados</option>
                <option value="Cancelled">Cancelados</option>
                <option value="Revoked">Revogados</option>
              </select>
              <ChevronDown className={cn('absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none', subtext)} />
            </div>

            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors',
                lightTheme ? 'border-gray-300 text-gray-600 hover:bg-gray-100' : 'border-gray-700 text-gray-400 hover:bg-gray-800'
              )}
            >
              <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
              Filtrar
            </button>

            {/* Lista | Calendário. Switching to the month widens the loaded range to that whole
                month — the from/to pair above belongs to the list. */}
            <div className={cn('flex items-center rounded-lg border overflow-hidden ml-auto',
              lightTheme ? 'border-gray-300' : 'border-gray-700')}>
              <button
                onClick={() => setBookingsView('list')}
                className={cn('flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors',
                  bookingsView === 'list'
                    ? 'bg-blue-600 text-white'
                    : lightTheme ? 'text-gray-600 hover:bg-gray-100' : 'text-gray-400 hover:bg-gray-800')}
              >
                <List className="h-3.5 w-3.5" />
                Lista
              </button>
              <button
                onClick={() => { setBookingsView('calendar'); showMonth(monthAnchor); }}
                className={cn('flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors',
                  bookingsView === 'calendar'
                    ? 'bg-blue-600 text-white'
                    : lightTheme ? 'text-gray-600 hover:bg-gray-100' : 'text-gray-400 hover:bg-gray-800')}
              >
                <CalendarDays className="h-3.5 w-3.5" />
                Calendário
              </button>
            </div>
          </div>

          {bookingsView === 'calendar' ? (
            <div data-testid="bookings-calendar" className="flex-1 flex flex-col min-h-0">
              <BookingsCalendar
                bookings={filteredBookings}
                areas={areas}
                monthAnchor={monthAnchor}
                onMonthChange={showMonth}
                onSelectBooking={setDetailBooking}
                lightTheme={lightTheme}
              />
            </div>
          ) : (
          <>
          {/* Table header */}
          <div className={cn('flex-shrink-0 hidden md:grid grid-cols-[2rem_1fr_1fr_1fr_1fr_5rem] gap-4 px-4 py-2 border-b',
            lightTheme ? 'bg-gray-50 border-gray-100 text-gray-500' : 'bg-gray-900/50 border-gray-800/60 text-gray-500'
          )}>
            <div />
            <span className="text-xs font-semibold uppercase tracking-wider">Morador</span>
            <span className="text-xs font-semibold uppercase tracking-wider">Área</span>
            <span className="text-xs font-semibold uppercase tracking-wider">Data / Hora</span>
            <span className="text-xs font-semibold uppercase tracking-wider">Status</span>
            <span className="text-xs font-semibold uppercase tracking-wider text-right">Ações</span>
          </div>

          {/* Rows */}
          <div className="flex-1 overflow-y-auto">
            {filteredBookings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <CalendarDays className={cn('h-10 w-10', lightTheme ? 'text-gray-300' : 'text-gray-700')} />
                <p className={cn('text-sm', subtext)}>Nenhum agendamento encontrado</p>
              </div>
            ) : (
              filteredBookings.map(b => (
                <BookingRow
                  key={b.id}
                  booking={b}
                  areas={areas}
                  lightTheme={lightTheme}
                  onApprove={handleApprove}
                  onRevoke={handleRevoke}
                  onCancel={handleCancel}
                  onViewPerson={openPersonDetail}
                  onOpen={setDetailBooking}
                  processing={processing}
                />
              ))
            )}
          </div>
          </>
          )}
        </div>
      )}

      {/* ── Area Detail Modal ───────────────────────────────────────────────── */}
      {selectedArea && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedAreaId(null)}
        >
          <div
            className={cn(
              'w-full max-w-2xl rounded-2xl shadow-2xl border flex flex-col overflow-hidden',
              lightTheme ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-800'
            )}
            style={{ maxHeight: '88vh' }}
            onClick={e => e.stopPropagation()}
          >
            <AreaDetailView
              area={selectedArea}
              lightTheme={lightTheme}
              refreshKey={detailRefreshKey}
              onBack={() => setSelectedAreaId(null)}
              onBook={() => setBookingModal({ open: true, areaId: selectedArea.id })}
              onApprove={handleApprove}
              onRevoke={handleRevoke}
              onCancel={handleCancel}
              onViewPerson={openPersonDetail}
              onOpenBooking={setDetailBooking}
              processing={processing}
            />
          </div>
        </div>
      )}

      {/* ── Reserva Detail Modal ───────────────────────────────────────────── */}
      {detailBooking && (
        <div data-testid="reserva-detail-modal">
          <ReservaDetailModal
            booking={detailBooking}
            areas={areas}
            allBookings={bookings}
            lightTheme={lightTheme}
            processing={processing.has(detailBooking.id)}
            onApprove={handleApprove}
            onRevoke={handleRevoke}
            onCancel={handleCancel}
            onViewPerson={openPersonDetail}
            onUpdated={() => fetchData(true)}
            onClose={() => setDetailBooking(null)}
          />
        </div>
      )}

      {/* ── Nova Reserva Modal ─────────────────────────────────────────────── */}
      {bookingModal.open && (
        <NovaReservaModal
          customerId={customerId}
          areas={areas.filter(a => a.is_active !== false)}
          initialAreaId={bookingModal.areaId || undefined}
          lightTheme={lightTheme}
          onClose={() => setBookingModal({ open: false, areaId: '' })}
          onSuccess={() => {
            setBookingModal({ open: false, areaId: '' });
            fetchData(true);
            bumpDetail();
          }}
        />
      )}

      {/* ── Revoke Modal ────────────────────────────────────────────────────── */}
      {revokeModal.open && revokeModal.booking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className={cn('w-full max-w-md rounded-2xl shadow-2xl border', lightTheme ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-700')}>
            <div className={cn('flex items-center justify-between px-6 py-4 border-b', lightTheme ? 'border-gray-200' : 'border-gray-700')}>
              <h3 className={cn('text-base font-bold text-red-500')}>Revogar Reserva</h3>
              <button
                onClick={() => setRevokeModal({ open: false, booking: null, reason: '', submitting: false })}
                className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-100 text-gray-500' : 'hover:bg-gray-800 text-gray-400')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-6 py-4 flex flex-col gap-4">
              <div className={cn('p-3 rounded-lg text-sm', lightTheme ? 'bg-gray-50' : 'bg-gray-800')}>
                <p className={cn('font-medium', text)}>{personName(revokeModal.booking)}</p>
                <p className={subtext}>{areas.find(a => a.id === revokeModal.booking!.common_area_id)?.name} · {fmtDateTime(revokeModal.booking.start_time)}</p>
              </div>
              <div>
                <label className={cn('text-xs font-medium block mb-1.5', lightTheme ? 'text-gray-700' : 'text-gray-300')}>
                  Motivo da revogação *
                </label>
                <textarea
                  rows={3}
                  value={revokeModal.reason}
                  onChange={e => setRevokeModal(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Descreva o motivo…"
                  className={cn('w-full px-3 py-2 text-sm rounded-lg border resize-none focus:outline-none', inputCls)}
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setRevokeModal({ open: false, booking: null, reason: '', submitting: false })}
                  className={cn('px-4 py-2 rounded-lg text-sm font-semibold border transition-colors',
                    lightTheme ? 'border-gray-300 text-gray-600 hover:bg-gray-100' : 'border-gray-700 text-gray-400 hover:bg-gray-800'
                  )}
                >
                  Cancelar
                </button>
                <button
                  onClick={submitRevoke}
                  disabled={!revokeModal.reason.trim() || revokeModal.submitting}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
                >
                  {revokeModal.submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Revogar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AreasComuns;
